import React, { useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { BarChart as BarChartIcon } from '@mui/icons-material';
import { useNotify } from 'react-admin';
import EventMappingChart from './EventMappingChart';
import { useFilterList } from '../../../context/FilterListProvider';
import type { IData } from '../../../types';

interface MappingReportButtonProps {
    dataProvider: IData;
}

/**
 * MappingReportButton - Button component that fetches and displays event mapping statistics
 * Reads filter values from the list context and calls the GetEventMappingReport API
 */
const MappingReportButton = ({ dataProvider }: MappingReportButtonProps) => {
    const notify = useNotify();
    const [filterList] = useFilterList();
    if (!filterList) return null;
    const { Mapped_By_contains, Mapped_Time_ge, Mapped_Time_le, adminonly, Mapped } = filterList;
    const [state, setState] = useState({
        isLoading: false,
        chartOpen: false,
        chartData: null as IData[] | null
    });

    const handleShowReport = () => {
        // Extract filter values for the API call
        const filters = { Mapped_By_contains, Mapped_Time_ge, Mapped_Time_le, adminonly };

        setState(prev => ({ ...prev, isLoading: true, chartOpen: true }));
        // Call the GetEventMappingReport API endpoint
        dataProvider.postData('ListingsMapping', 'GetEventMappingReport', filters)
            .then((response: IData) => {
                setState(prev => ({
                    ...prev,
                    isLoading: false,
                    chartData: response.data || []
                }));
            })
            .catch((error: IData) => {
                notify(`Failed to load mapping report: ${error.message || error}`, {
                    type: 'error'
                });
                setState(prev => ({ ...prev, isLoading: false, chartData: [] }));
            });
    };

    const handleCloseChart = () => {
        setState(prev => ({ ...prev, chartOpen: false }));
    };

    return (
        <>
            {Mapped === 1 && !!Mapped_Time_ge && <Button
                variant='contained'
                size='small'
                onClick={handleShowReport}
                disabled={state.isLoading}
                startIcon={state.isLoading ? <CircularProgress size={20} /> : <BarChartIcon />}
                style={{ marginLeft: 8 }}
            >
                Mapping Report
            </Button>
            }
            <EventMappingChart
                open={state.chartOpen}
                handleClose={handleCloseChart}
                data={state.chartData}
                isLoading={state.isLoading}
                filterList={filterList}
            />
        </>
    );
};

export default MappingReportButton;
